import { type SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'

import { usePutQuotingProspectsId } from 'utils/web_api'
import { type PutQuotingProspectsId, PutQuotingProspectsIdReasonsForChangingInsuranceItem, QuotingProspectQuoteRequestStatus } from 'utils/web_api.schemas'

import { QuoteRequestStep } from '..'
import { BusinessDetailsForm, type BusinessFormData } from '../BusinessDetailsForm'
import { useProspect } from '../ProspectProvider'
import { useQuoteRequestProgress } from '../useQuoteRequestProgress'

interface ClientDetailsPageProps {
  dashboardUrl: string
}

export function ClientDetailsPage ({ dashboardUrl }: ClientDetailsPageProps) {
  const { prospect, refetch } = useProspect()
  const { completeStep } = useQuoteRequestProgress({
    step: QuotingProspectQuoteRequestStatus.client_details,
    onSuccess: () => {
      refetch()
      navigate(`../${QuoteRequestStep.CENSUS_UPLOAD}`)
    }
  })
  const navigate = useNavigate()

  const { mutate: updateProspect } = usePutQuotingProspectsId({
    mutation: {
      onSuccess: completeStep
    }
  })

  const form = useForm<BusinessFormData>({
    defaultValues: {
      businessName: prospect?.name,
      offersBenefits: prospect?.offers_health_insurance_today,
      coverageStartDate: prospect?.health_insurance_effective_date,
      reasons: {
        [PutQuotingProspectsIdReasonsForChangingInsuranceItem.make_employees_happier]: prospect?.reasons_for_changing_insurance?.includes(PutQuotingProspectsIdReasonsForChangingInsuranceItem.make_employees_happier) ?? false,
        [PutQuotingProspectsIdReasonsForChangingInsuranceItem.avoid_rate_increases]: prospect?.reasons_for_changing_insurance?.includes(PutQuotingProspectsIdReasonsForChangingInsuranceItem.avoid_rate_increases) ?? false,
        [PutQuotingProspectsIdReasonsForChangingInsuranceItem.best_benefits]: prospect?.reasons_for_changing_insurance?.includes(PutQuotingProspectsIdReasonsForChangingInsuranceItem.best_benefits) ?? false,
        [PutQuotingProspectsIdReasonsForChangingInsuranceItem.visibility_control_of_healthcare_costs]: prospect?.reasons_for_changing_insurance?.includes(PutQuotingProspectsIdReasonsForChangingInsuranceItem.visibility_control_of_healthcare_costs) ?? false,
        other: !!prospect?.other_reason_for_changing_insurance,
      },
      otherReason: prospect?.other_reason_for_changing_insurance ?? ''
    },
    mode: 'onChange'
  })

  const onSubmit: SubmitHandler<BusinessFormData> = (data) => {
    if (!prospect) {
      return
    }

    const prospectData: Partial<PutQuotingProspectsId> = {
      name: data.businessName,
      offers_health_insurance_today: data.offersBenefits,
      health_insurance_effective_date: data.coverageStartDate,
      reasons_for_changing_insurance: Object.keys(data.reasons)
        .filter(key => key !== 'other')
        .map(key => key as PutQuotingProspectsIdReasonsForChangingInsuranceItem)
        .filter(key => data.reasons[key]),
      other_reason_for_changing_insurance: data.otherReason
    }

    updateProspect({
      id: prospect.id,
      data: prospectData
    })
  }

  const onBack = () => {
    // Exit the quote request flow
    window.location.href = dashboardUrl
  }

  return (
    <BusinessDetailsForm
      form={form}
      title='Basic Details'
      backText='Back'
      submitText='Next'
      onBack={onBack}
      onSubmit={onSubmit}
    />
  )
}
